import {
	BellIcon,
	ChevronDownIcon,
	HelpCircleIcon,
	MoonIcon,
	SearchIcon,
	SunIcon,
} from "lucide-react";
import { cn } from "../lib/utils.js";

export interface BreadcrumbItem {
	label: string;
	href?: string;
}

export interface AdminTopbarProps {
	breadcrumbs: BreadcrumbItem[];
	searchPlaceholder?: string;
	periodLabel?: string;
	userInitials: string;
	userName: string;
	userRole: string;
	notificationCount?: number;
	isDark?: boolean;
	onToggleTheme?: () => void;
	className?: string;
}

export function AdminTopbar({
	breadcrumbs,
	searchPlaceholder = "Search…",
	periodLabel,
	userInitials,
	userName,
	userRole,
	notificationCount = 0,
	isDark = false,
	onToggleTheme,
	className,
}: AdminTopbarProps) {
	return (
		<div className={cn("flex w-full items-center gap-4 px-6", className)}>
			{/* Breadcrumbs */}
			<nav className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
				{breadcrumbs.map((crumb, i) => (
					<span key={crumb.label} className="flex items-center gap-1.5">
						{i > 0 && <span className="text-muted-foreground/40">/</span>}
						{i === breadcrumbs.length - 1 ? (
							<span className="font-semibold text-foreground">
								{crumb.label}
							</span>
						) : (
							<a
								href={crumb.href ?? "#"}
								className="hover:text-foreground transition-colors"
							>
								{crumb.label}
							</a>
						)}
					</span>
				))}
			</nav>

			{/* Search */}
			<div className="relative ml-6 max-w-[420px] flex-1">
				<SearchIcon className="absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-muted-foreground/60" />
				<input
					type="search"
					placeholder={searchPlaceholder}
					className={cn(
						"h-9 w-full rounded-[10px] border border-border bg-muted/60 pl-9 pr-16",
						"text-[13px] text-foreground placeholder:text-muted-foreground/50",
						"outline-none focus:border-secondary focus:ring-2",
						"focus:ring-secondary/20 transition-shadow",
					)}
				/>
				<span className="absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10.5px] text-muted-foreground">
					⌘K
				</span>
			</div>

			{/* Actions */}
			<div className="ml-auto flex items-center gap-2">
				{periodLabel && (
					<button
						type="button"
						className="flex h-9 items-center gap-1.5 rounded-[10px] border border-border bg-card px-3 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/60"
					>
						{periodLabel}
						<ChevronDownIcon className="h-3 w-3 text-muted-foreground" />
					</button>
				)}

				<div className="mx-1 h-6 w-px bg-border" />

				{onToggleTheme && (
					<IconButton onClick={onToggleTheme} title="Toggle theme">
						{isDark ? (
							<SunIcon className="h-[17px] w-[17px]" />
						) : (
							<MoonIcon className="h-[17px] w-[17px]" />
						)}
					</IconButton>
				)}
				<IconButton title="Help">
					<HelpCircleIcon className="h-[17px] w-[17px]" />
				</IconButton>
				<IconButton title="Notifications" hasDot={notificationCount > 0}>
					<BellIcon className="h-[17px] w-[17px]" />
				</IconButton>

				<div className="mx-1 h-6 w-px bg-border" />

				{/* User chip */}
				<button
					type="button"
					className="flex items-center gap-2.5 rounded-full border border-border bg-card py-1 pl-1 pr-2.5 transition-colors hover:bg-muted/60"
				>
					<div
						className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold"
						style={{
							background:
								"linear-gradient(135deg, var(--gold, #FFC832) 0%, #E8A93B 100%)",
							color: "var(--ink, #121A37)",
						}}
					>
						{userInitials}
					</div>
					<div className="text-left">
						<div className="text-[12.5px] font-semibold leading-tight text-foreground">
							{userName}
						</div>
						<div className="text-[10.5px] text-muted-foreground">
							{userRole}
						</div>
					</div>
					<ChevronDownIcon className="ml-0.5 h-3.5 w-3.5 text-muted-foreground" />
				</button>
			</div>
		</div>
	);
}

function IconButton({
	children,
	hasDot,
	...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { hasDot?: boolean }) {
	return (
		<button
			type="button"
			{...props}
			className="relative flex h-9 w-9 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
		>
			{children}
			{hasDot && (
				<span
					className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full border-2 border-card"
					style={{ background: "var(--color-secondary, var(--gold, #FFC832))" }}
				/>
			)}
		</button>
	);
}
